import { useContentSelectieContext } from "@/context/ContentSelectieContext";
import { formatTitel, sortHoofdstukken } from "@/lib/utils";
import SelectionPill from "./SelectionPill";
import { useCallback, useMemo } from "react";
import { useHuiswerkModusContext } from "@/context/HuiswerkModusContext";
import { useAppContext } from "@/context/AppContext";
import { Hoofdstuk } from "@/lib/types";
import { useAuthContext } from "@/context/AuthContext";
import { useRouter } from "next/navigation";
import { useLibraryContext } from "@/context/LibraryContext";
import type { ContentSelectFlow } from "./types";

export default function HoofdstukSelectV2({
  flow = "full"
}: {
  flow?: ContentSelectFlow;
}) {
  const { hoofdstukken, selectModus } = useAppContext();
  const { isPremium } = useAuthContext();
  const { setSelectedHoofdstuk } = useContentSelectieContext();
  const { clearHuiswerkSession } = useHuiswerkModusContext();
  const { setSideMenuExpanded } = useLibraryContext();

  const router = useRouter();

  const hoofdstukClick = useCallback(
    (id: number) => {
      // A chapter change invalidates any existing homework set before the new
      // flow decides whether to start directly or show the full mode chooser.
      clearHuiswerkSession();
      setSelectedHoofdstuk(id);
      if (flow === "quickstart" || !isPremium) {
        selectModus(
          "uitleg",
          flow === "quickstart" ? "quickstart" : "mode_card"
        );
        setSideMenuExpanded(true);
        router.push("/library");
      }
    },
    [
      clearHuiswerkSession,
      flow,
      isPremium,
      router,
      selectModus,
      setSelectedHoofdstuk,
      setSideMenuExpanded
    ]
  );

  const hoofdstukGroepen: Hoofdstuk[][] = useMemo(() => {
    const groepen: Record<number, Hoofdstuk[]> = {};

    hoofdstukken.forEach((h) => {
      if (!groepen[h.boek]) groepen[h.boek] = [];
      groepen[h.boek].push(h);
    });

    return Object.values(groepen);
  }, [hoofdstukken]);

  return (
    <>
      {/* Loading skeleton while chapters are being fetched */}
      {hoofdstukGroepen.length === 0 && (
        <div className="flex flex-col gap-6 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <div className="h-4 w-16 bg-gray-200 rounded" />
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-10 w-32 bg-gray-200 rounded-xl" />
            ))}
          </div>
        </div>
      )}

      {/* Actual chapters */}
      {hoofdstukGroepen.length > 0 && (
        <div className="flex flex-col gap-6">
          {hoofdstukGroepen.map((groep) => (
            <div key={`deel_${groep[0].boek}`} className="flex flex-col gap-3">
              {/* Styled header for each Deel */}
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-sm font-semibold text-gray-500 px-2">
                  Deel {groep[0].boek}
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {sortHoofdstukken(groep).map((hoofdstuk) => {
                  const fullTitle = formatTitel(hoofdstuk.titel);
                  const shortTitle = fullTitle
                    .replace(/Gemengde\s+opgaven/i, "Gem Opg")
                    .replace(/Hoofdstuk\s+Keuze/i, "HK")
                    .replace(/Hoofdstuk\s+(\d+)/i, "H$1");

                  return (
                    <SelectionPill
                      key={hoofdstuk.id}
                      onClick={() => hoofdstukClick(hoofdstuk.id)}
                      size="small"
                      ariaLabel={`Selecteer ${fullTitle} van deel ${hoofdstuk.boek}`}
                    >
                      <span className="block truncate max-w-[6rem] md:max-w-none">
                        <span className="md:hidden">{shortTitle}</span>
                        <span className="hidden md:inline">{fullTitle}</span>
                      </span>
                    </SelectionPill>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
